import { defineStore } from 'pinia'
import { computed, reactive, type WritableComputedRef } from 'vue'

/**
 * What a member arranged on the Pilotage pages, kept per project for the
 * session: filters, folded and unfolded groups, open cards (MYAC-1522 3b,
 * front-data skill: « state that must outlive a route change lives in the
 * store, keyed by project »).
 *
 * Each section becomes its own page, so leaving one unmounts it; this store is
 * what brings a member back to the page as they left it. Open dialogs and
 * popovers are not kept: they close with the page.
 *
 * Named slots, not a schema: a component asks for `slot(pid, 'compliance.filter',
 * () => initial)` where it used to write `ref(initial)`, so a panel keeps its own
 * shapes (Set, Map, Record) and only its storage moves.
 */
export const usePilotageViewStore = defineStore('pilotageView', () => {
  const byProject = reactive(new Map<string, Map<string, unknown>>())

  function slot<T>(projectId: string, key: string, initial: () => T): WritableComputedRef<T> {
    let slots = byProject.get(projectId)
    if (!slots) {
      byProject.set(projectId, new Map())
      slots = byProject.get(projectId)!
    }
    if (!slots.has(key)) slots.set(key, initial())
    const held = slots
    return computed({
      get: () => held.get(key) as T,
      set: (value: T) => { held.set(key, value) },
    })
  }

  /**
   * Read a slot without creating it: for a reader that is not the slot's
   * owner (the project menu reading what the Pilotage page announced), so a
   * computed can read it without writing the store.
   */
  function peek<T>(projectId: string, key: string): T | undefined {
    return byProject.get(projectId)?.get(key) as T | undefined
  }

  /** Forget a project's arrangement (tests; a project removed from the account). */
  function forget(projectId: string): void {
    byProject.delete(projectId)
  }

  return { slot, peek, forget }
})
